import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, CircleSlash, Download } from 'lucide-react';
import { useSeo, Reveal, DemoButton, Eyebrow, H2 } from './site/ui';
import { SwitchForm } from './site/SwitchForm';
import { TourButton } from './site/TourDialog';
import { SWITCH_LANGS, checklistPdf, switchCopy } from './site/switch';

const LangSwitch = ({ lang }) => (
  <nav className="mb-6 inline-flex rounded-full border border-[#cbd5e1] bg-[#ffffff] p-1" aria-label="Language" data-testid="switch-lang">
    {SWITCH_LANGS.map(([k, label, to]) => (
      <Link key={k} to={to} hrefLang={k} aria-current={lang === k ? 'page' : undefined}
        className={`rounded-full px-3.5 py-1 text-sm font-semibold transition-colors ${lang === k ? 'bg-[#0f172a] text-[#f8fafc]' : 'text-[#475569] hover:text-[#0f172a]'}`} data-testid={`switch-lang-${k}`}>{label}</Link>
    ))}
  </nav>
);

const Hero = ({ t, lang }) => (
  <section className="mx-auto max-w-7xl px-5 pb-14 pt-32 sm:px-8" data-testid="switch-hero">
    <Reveal>
      <LangSwitch lang={lang} />
      <Eyebrow>{t.hero.eyebrow}</Eyebrow>
      <h1 className="dl-display mt-4 max-w-4xl text-4xl font-semibold leading-[1.15] tracking-tight text-[#0f172a] sm:text-5xl lg:text-6xl" data-testid="switch-title">{t.hero.title}</h1>
      <p className="mt-5 max-w-2xl text-base text-[#475569] md:text-lg">{t.hero.sub}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href="#switch-request" className="inline-flex items-center gap-2 rounded-full bg-[#0f766e] px-6 py-3.5 text-[15px] font-semibold text-[#ffffff] transition-transform hover:-translate-y-0.5" data-testid="switch-hero-start">{t.hero.start} <ArrowRight size={16} /></a>
        <DemoButton testid="switch-hero-call">{t.hero.call}</DemoButton>
        <TourButton lang={lang} testid="switch-watch-tour" />
      </div>
    </Reveal>
    <div className="mt-12 grid gap-3 sm:grid-cols-3" data-testid="switch-offer">
      {t.offer.map(([h, d], i) => (
        <Reveal key={h} delay={i * 90} className="rounded-3xl bg-[#ffffff] p-6">
          <p className="dl-display text-2xl font-semibold text-[#0f766e]">{h}</p>
          <p className="mt-1 text-sm text-[#334155]">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Move = ({ t }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="switch-move">
    <Eyebrow>{t.eyebrow}</Eyebrow><H2>{t.title}</H2>
    <div className="mt-10 grid gap-4 md:grid-cols-3">
      {t.cards.map(([h, items], i) => (
        <Reveal key={h} delay={i * 80} className="rounded-3xl border border-[#e2e8f0] bg-[#ffffff] p-6" data-testid={`switch-move-${i}`}>
          <h3 className="dl-display text-xl font-semibold text-[#0f172a]">{h}</h3>
          <ul className="mt-4 space-y-2">{items.map((x) => <li key={x} className="flex gap-2 text-sm text-[#334155]"><Check size={16} className="mt-0.5 shrink-0 text-[#0f766e]" />{x}</li>)}</ul>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-4 flex gap-3 rounded-3xl bg-[#fef3c7]/60 p-6" data-testid="switch-stays">
      <CircleSlash size={18} className="mt-0.5 shrink-0 text-[#b45309]" />
      <p className="text-sm text-[#334155]"><b className="text-[#0f172a]">{t.stays_label}</b> {t.stays}</p>
    </Reveal>
  </section>
);

const Checklist = ({ t, lang }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" id="switch-checklist" data-testid="switch-checklist">
    <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr]">
      <div>
        <Eyebrow>{t.eyebrow}</Eyebrow><H2>{t.title}</H2>
        <p className="mt-4 max-w-md text-[#475569]">{t.tip}</p>
        <a href={checklistPdf(lang)} download className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#cbd5e1] px-5 py-2.5 text-sm font-semibold text-[#0f172a] hover:border-[#0f172a]" data-testid="switch-download-checklist"><Download size={15} /> {t.download}</a>
      </div>
      <ol className="space-y-2 rounded-3xl bg-[#ffffff] p-4 sm:p-6">
        {t.items.map(([h, d], i) => (
          <li key={h} className="flex gap-4 border-b border-dashed border-[#e2e8f0] py-3 last:border-0" data-testid={`switch-check-${i}`}>
            <span className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 border-[#94a3b8]" aria-hidden />
            <div><p className="font-semibold text-[#0f172a]">{i + 1}. {h}</p><p className="text-sm text-[#64748b]">{d}</p></div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Timeline = ({ t }) => (
  <section className="bg-[#0b1220] py-20 sm:py-28" data-testid="switch-timeline">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow dark>{t.eyebrow}</Eyebrow><H2 dark>{t.title}</H2>
      <ol className="mt-12 grid gap-4 md:grid-cols-5">
        {t.steps.map(([h, who, when, d], i) => (
          <Reveal as="li" key={h} delay={i * 80} className="rounded-2xl border border-[#1e293b] p-5" data-testid={`switch-step-${i}`}>
            <span className="dl-display flex h-8 w-8 items-center justify-center rounded-full bg-[#5eead4] text-sm font-semibold text-[#0b1220]">{i + 1}</span>
            <p className="dl-display mt-4 text-lg font-semibold text-[#f8fafc]">{h}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#5eead4]">{who} · {when}</p>
            <p className="mt-2 text-sm text-[#94a3b8]">{d}</p>
          </Reveal>
        ))}
      </ol>
      <div className="mt-12 rounded-3xl bg-[#111a2e] p-6 sm:p-8" data-testid="switch-cutover">
        <p className="dl-display text-xl font-semibold text-[#f8fafc]">{t.cutover_title}</p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">{t.cutover.map((c) => <li key={c} className="flex gap-2 text-sm text-[#cbd5e1]"><Check size={16} className="mt-0.5 shrink-0 text-[#5eead4]" />{c}</li>)}</ul>
      </div>
    </div>
  </section>
);

const Faq = ({ t }) => (
  <section className="mx-auto max-w-4xl px-5 py-20 sm:px-8" data-testid="switch-faq">
    <Eyebrow>{t.eyebrow}</Eyebrow><H2>{t.title}</H2>
    <div className="mt-8 divide-y divide-[#e2e8f0]">
      {t.items.map(([q, a], i) => (
        <details key={q} className="group py-4" data-testid={`switch-faq-${i}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-[#0f172a]">{q}<span className="text-xl text-[#0f766e] transition-transform group-open:rotate-45">+</span></summary>
          <p className="mt-2 text-[#475569]">{a}</p>
        </details>
      ))}
    </div>
  </section>
);

export default function DrivelineSwitch({ lang = 'en' }) {
  const t = switchCopy(lang);
  useSeo({ title: t.meta.title, description: t.meta.description });
  return (
    <div lang={lang} data-lang={lang} data-testid="switch-page">
      <Hero t={t} lang={lang} /><Move t={t.move} /><Checklist t={t.checklist} lang={lang} /><Timeline t={t.timeline} /><Faq t={t.faq} />
      <section id="switch-request" className="mx-auto max-w-4xl scroll-mt-24 px-5 pb-24 sm:px-8" data-testid="switch-request">
        <Eyebrow>{t.request.eyebrow}</Eyebrow><H2>{t.request.title}</H2>
        <p className="mb-8 mt-3 text-[#475569]">{t.request.sub}</p>
        <SwitchForm key={lang} lang={lang} />
      </section>
    </div>
  );
}
